import { useState, useEffect } from 'react';
import api from '../../api';
import { useAuth } from '../../context/AuthContext';
import MouldForm from './MouldForm';
import RecipeForm from './RecipeForm';

// ── Helpers ──────────────────────────────────────────────────
function parseReq(val) {
  if (!val) return [];
  try { const p = JSON.parse(val); return Array.isArray(p) ? p : [val]; }
  catch { return [val]; }
}

const REQ_PRESETS = {
  dim:      ['Tight tolerances', 'Critical dimensions', 'Go/no-go gauge required', 'Flatness critical', 'True position critical'],
  visual:   ['No flash', 'No sink marks', 'No weld lines visible', 'No short shots', 'Colour match required', 'Gloss surface', 'Matt surface', 'No scratches', 'No contamination'],
  assembly: ['Snap fit', 'Press fit', 'Clearance fit', 'No burrs on mating faces', 'Assembly direction critical', 'Orientation marked'],
  handling: ['Fragile', 'Sharp edges — gloves required', 'Clean room handling', 'ESD sensitive', 'No direct contact', 'Keep dry'],
};

export const ATTENTION_WORDS = { 1: 'little to none', 2: 'a little', 3: 'some', 4: 'a lot', 5: 'a whole person' };
// The time behind each level, as the part's detail page shows it (29 Sep).
export const ATTENTION_HOURS = { 1: '0–0.5 h a shift', 2: '0.5–1 h a shift', 3: '1–3 h a shift', 4: '3–6 h a shift', 5: 'the whole shift' };

export default function PartForm({ part, onClose, onSaved }) {
  const { isDirector } = useAuth();
  const canEditPackaging = isDirector();

  const [machines, setMachines]     = useState([]);
  const [moulds, setMoulds]         = useState([]);
  const [allRecipes, setAllRecipes] = useState([]);
  const [colours, setColours]       = useState([]);
  const [products, setProducts]     = useState([]);
  const [containers, setContainers] = useState([]);   // what parts can be packed into
  const [loaded, setLoaded]         = useState(false);

  const [saving, setSaving]                 = useState(false);
  const [showMouldForm, setShowMouldForm]   = useState(false);
  const [showRecipeForm, setShowRecipeForm] = useState(false);

  const [productId, setProductId]           = useState('');
  const [productQty, setProductQty]         = useState(1);
  const [showNewProduct, setShowNewProduct] = useState(false);
  const [newPCode, setNewPCode]             = useState('');
  const [newPName, setNewPName]             = useState('');

  const [form, setForm] = useState({
    name:                        part?.name || '',
    part_number:                 part?.part_number || '',
    customer:                    part?.customer || '',
    status:                      part?.status || 'active',
    discontinued_date:           part?.discontinued_date || '',
    colour:                      part?.colour || '',
    is_critical:                 part?.is_critical || false,
    primary_machine_id:          part?.primary_machine_id || '',
    secondary_machine_ids:       part?.secondary_machine_ids || [],
    mould_id:                    part?.mould_id || '',
    target_cycle_s:              part?.target_cycle_s ?? '',
    shot_weight_g:               part?.shot_weight_g ?? '',
    part_weight_g:               part?.part_weight_g ?? '',
    sprue_weight_g:              part?.sprue_weight_g ?? '',
    scrap_pct:                   part?.scrap_pct ?? '',
    attention_level:             part?.attention_level ?? '',
    collector_box_qty:           part?.collector_box_qty ?? '',
    dim_requirements:            parseReq(part?.dim_requirements),
    visual_requirements:         parseReq(part?.visual_requirements),
    assembly_requirements:       parseReq(part?.assembly_requirements),
    handling_requirements:       parseReq(part?.handling_requirements),
    packaging_requirements:      part?.packaging_requirements || '',
    packaging_layers:            part?.packaging_layers || [],
    packing_count_method:        part?.packing_count_method || '',
    packing_place:               part?.packing_place || '',
    ...wrappingOf(part),
    recipe_ids:                  part?.part_recipes?.map(pr => pr.recipe?.id).filter(Boolean) || [],
    weight_log_interval_minutes: part?.weight_log_interval_minutes ?? 60,
    description:                 part?.description || '',
  });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    Promise.all([
      api.get('/api/v1/machines'),
      api.get('/api/v1/moulds'),
      api.get('/api/v1/recipes').catch(() => ({ data: [] })),
      api.get('/api/v1/colours').catch(() => ({ data: [] })),
      api.get('/api/v1/products').catch(() => ({ data: [] })),
      api.get('/api/v1/packaging-containers').catch(() => ({ data: [] })),
    ]).then(([m, mo, r, cl, pr, pc]) => {
      setContainers(pc.data || []);
      setMachines(m.data);
      setMoulds(mo.data);
      setAllRecipes(r.data);
      setColours(cl.data);
      setProducts(pr.data);
      const cp = (pr.data || []).find(p => p.product_parts?.some(pp => pp.part?.id === part?.id));
      if (cp) {
        setProductId(cp.id);
        const pp = cp.product_parts.find(pp => pp.part?.id === part?.id);
        setProductQty(pp?.qty_per_assembly || 1);
      }
      setLoaded(true);
    });
  }, [part?.id]);

  const secondaryIds    = form.secondary_machine_ids || [];
  const usedIds         = new Set([form.primary_machine_id ? parseInt(form.primary_machine_id) : null, ...secondaryIds].filter(Boolean));
  const availableMachines = machines.filter(m => !usedIds.has(m.id));
  const machineName     = id => machines.find(m => m.id === id)?.name || `#${id}`;
  const selectedMould   = moulds.find(m => m.id === parseInt(form.mould_id));
  const colourHex       = form.colour ? colours.find(c => c.name === form.colour)?.hex : null;

  // Weight hint: part_weight × cavities + sprue ≈ shot_weight
  const cavities     = selectedMould?.cavities;
  const expectedShot = cavities && form.part_weight_g !== '' && form.sprue_weight_g !== ''
    ? ((parseFloat(form.part_weight_g) || 0) * cavities + (parseFloat(form.sprue_weight_g) || 0)).toFixed(1)
    : null;
  const shotDiff = expectedShot && form.shot_weight_g !== ''
    ? Math.abs(parseFloat(form.shot_weight_g) - parseFloat(expectedShot))
    : null;

  function addSecondary(id) { if (!id) return; set('secondary_machine_ids', [...secondaryIds, parseInt(id)]); }
  function removeSecondary(id) { set('secondary_machine_ids', secondaryIds.filter(x => x !== id)); }
  function moveSecondary(idx, dir) {
    const next = [...secondaryIds]; const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    set('secondary_machine_ids', next);
  }

  // "Other…" in a packaging layer: the typed container joins the list for
  // everyone (or is the one already there under that name).
  function addContainer(name) {
    return api.post('/api/v1/packaging-containers', { name }).then(r => {
      setContainers(cs => (cs.some(c => c.id === r.data.id) ? cs
        : [...cs, r.data].sort((a, b) => (a.sort_order - b.sort_order) || a.name.localeCompare(b.name))));
      return r.data;
    });
  }

  function save() {
    if (!form.name) return;
    setSaving(true);
    const payload = {
      ...form,
      primary_machine_id:          form.primary_machine_id ? parseInt(form.primary_machine_id) : null,
      secondary_machine_ids:       secondaryIds,
      mould_id:                    form.mould_id ? parseInt(form.mould_id) : null,
      target_cycle_s:              form.target_cycle_s === '' ? null : (Number(String(form.target_cycle_s).replace(',', '.')) || null),
      weight_log_interval_minutes: form.weight_log_interval_minutes === '' ? null : parseInt(form.weight_log_interval_minutes),
      shot_weight_g:               form.shot_weight_g  === '' ? null : parseFloat(form.shot_weight_g),
      part_weight_g:               form.part_weight_g  === '' ? null : parseFloat(form.part_weight_g),
      sprue_weight_g:              form.sprue_weight_g === '' ? null : parseFloat(form.sprue_weight_g),
      scrap_pct:                   form.scrap_pct      === '' ? null : parseFloat(form.scrap_pct),
      attention_level:             form.attention_level === '' ? null : parseInt(form.attention_level),
      collector_box_qty:           form.collector_box_qty === '' ? null : parseInt(form.collector_box_qty),
      discontinued_date:           form.discontinued_date || null,
      colour:                      form.colour || null,
      dim_requirements:            form.dim_requirements.length      ? JSON.stringify(form.dim_requirements)      : null,
      visual_requirements:         form.visual_requirements.length   ? JSON.stringify(form.visual_requirements)   : null,
      assembly_requirements:       form.assembly_requirements.length ? JSON.stringify(form.assembly_requirements) : null,
      handling_requirements:       form.handling_requirements.length ? JSON.stringify(form.handling_requirements) : null,
    };
    // Parts per box and per pallet are worked out from the layers by the server.
    if (!canEditPackaging) { delete payload.packaging_layers; delete payload.packing_count_method; delete payload.packing_place; WRAP_KEYS.forEach(k => delete payload[k]); }

    const req = part
      ? api.put(`/api/v1/parts/${part.id}`, payload)
      : api.post('/api/v1/parts', payload);

    req.then(async (res) => {
      const savedPartId    = res.data.id;
      const currentProduct = products.find(p => p.product_parts?.some(pp => pp.part?.id === part?.id));
      const currentPP      = currentProduct?.product_parts?.find(pp => pp.part?.id === part?.id);
      try {
        if (showNewProduct && newPCode && newPName) {
          await api.post('/api/v1/products', { code: newPCode, name: newPName, parts: [{ part_id: savedPartId, qty: productQty }] });
        } else if (productId !== (currentProduct?.id || '')) {
          if (currentProduct?.id) await api.delete(`/api/v1/products/${currentProduct.id}/parts/${savedPartId}`);
          if (productId) await api.post(`/api/v1/products/${productId}/parts`, { part_id: savedPartId, qty_per_assembly: productQty });
        } else if (productId && productQty !== (currentPP?.qty_per_assembly || 1)) {
          await api.post(`/api/v1/products/${productId}/parts`, { part_id: savedPartId, qty_per_assembly: productQty });
        }
      } catch (e) { /* product linking non-fatal */ }
      onSaved();
    }).catch(() => setSaving(false));
  }

  // What each closed section holds, in one plain line (29 Sep): the values
  // that matter, and in amber the ones that matter and are missing. Each item
  // is text, { text, tone }, or nothing.
  // In a product (or about to be): its packaging section is storage instead.
  const goesIntoProduct = !!productId || (showNewProduct && !!newPCode);
  const selectedRecipes = form.recipe_ids.map(id => allRecipes.find(r => r.id === id)).filter(Boolean);
  const inProduct = products.find(p => String(p.id) === String(productId));
  const count = n => Number(n).toLocaleString('en-GB');
  const firstAndMore = list => (!list.length ? null : list.length === 1 ? list[0] : `${list[0]} +${list.length - 1} more`);
  const missing = text => ({ text, tone: 'warn' });
  const checks = form.dim_requirements.length + form.visual_requirements.length;
  const sum = {
    identity: [form.part_number, form.customer, form.colour, form.status === 'discontinued' && missing('Discontinued')],
    moulding: [
      form.primary_machine_id
        ? `${machineName(parseInt(form.primary_machine_id))}${secondaryIds.length ? ` + ${secondaryIds.length} backup${secondaryIds.length === 1 ? '' : 's'}` : ''}`
        : missing('no machine'),
      selectedMould
        ? `Mould ${selectedMould.ref}${selectedMould.cavities ? ` (${selectedMould.cavities} ${selectedMould.cavities === 1 ? 'cavity' : 'cavities'})` : ''}`
        : missing('no mould'),
      form.target_cycle_s !== '' ? `${form.target_cycle_s} s cycle` : missing('no cycle time'),
    ],
    material: [selectedRecipes.length ? firstAndMore(selectedRecipes.map(r => r.name)) : missing('no recipe')],
    atMachine: [
      form.attention_level !== ''
        ? `Attention ${form.attention_level} (${ATTENTION_WORDS[form.attention_level]})`
        : missing('attention not estimated'),
      form.collector_box_qty !== '' && `Collector ${count(form.collector_box_qty)} pcs`,
      firstAndMore(form.handling_requirements),
    ],
    quality: [
      form.is_critical && { text: 'Critical / safety part', tone: 'danger' },
      checks > 0 && `${checks} check${checks === 1 ? '' : 's'}`,
      form.weight_log_interval_minutes !== '' && `Weight logged every ${form.weight_log_interval_minutes} min`,
    ],
    product: [
      showNewProduct ? (newPName && `New product: ${newPName}`) : inProduct && `Part of ${inProduct.name}${productQty > 1 ? ` (×${productQty})` : ''}`,
      firstAndMore(form.assembly_requirements),
    ],
    packaging: [
      form.packaging_layers.length ? packagingChain(form.packaging_layers, containers) : null,
      form.packaging_layers.some(l => l.container_id == null) && missing('container not set'),
      form.packing_count_method && COUNT_WORDS[form.packing_count_method],
      form.packing_place && PACK_PLACE_WORDS[form.packing_place],
      form.packaging_requirements && 'packaging notes',
    ],
    notes: [form.description && (form.description.length > 60 ? `${form.description.slice(0, 60).trim()}…` : form.description)],
  };

  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={s.modal} onClick={e => e.stopPropagation()}>
        <div style={s.modalHeader}>
          <div style={s.modalTitle}>{part ? `Edit — ${part.name}` : 'Add New Part'}</div>
          <button style={s.closeBtn} onClick={onClose}>✕</button>
        </div>
        <div style={s.modalBody}>
          {!loaded ? (
            <div style={{ padding: '20px 0', color: '#6b7280', fontSize: '13px' }}>Loading…</div>
          ) : (
          <>
          {/* The sections follow the part's life (29 Sep): what it is, how it
              is made and from what, what running it asks of people, how it is
              checked, what it goes into, how it ships. A closed section says
              in one line what is in it. */}

          {/* ── IDENTITY ── */}
          <Section title="Identity" defaultOpen summary={sum.identity}>
            <div style={s.row2}>
              <Field label="Part name *" value={form.name}        onChange={v => set('name', v)} />
              <Field label="Part number" value={form.part_number} onChange={v => set('part_number', v)} />
            </div>
            <div style={s.row2}>
              <Field label="Customer" value={form.customer} onChange={v => set('customer', v)} />
              <div style={s.field}>
                <label style={s.label}>Status</label>
                <select style={s.input} value={form.status} onChange={e => { set('status', e.target.value); if (e.target.value !== 'discontinued') set('discontinued_date', ''); }}>
                  <option value="active">Active</option>
                  <option value="discontinued">Discontinued</option>
                </select>
                {form.status === 'discontinued' && (
                  <input type="date" style={{ ...s.input, marginTop: '4px' }} value={form.discontinued_date} onChange={e => set('discontinued_date', e.target.value)} />
                )}
              </div>
            </div>
            <div style={s.row2}>
              <div style={s.field}>
                <label style={s.label}>Colour</label>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  {colourHex && <div style={{ width: '18px', height: '18px', borderRadius: '50%', background: colourHex, flexShrink: 0, border: '1px solid #2a3040' }} />}
                  <select style={{ ...s.input, flex: 1 }} value={form.colour} onChange={e => set('colour', e.target.value)}>
                    <option value="">— Not specified —</option>
                    {colours.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                  </select>
                </div>
              </div>
            </div>
          </Section>

          {/* ── MOULDING ── */}
          <Section title="Moulding" summary={sum.moulding}>
            <div style={s.row2}>
              <div style={s.field}>
                <label style={s.label}>Primary machine</label>
                <select style={s.input} value={form.primary_machine_id} onChange={e => set('primary_machine_id', e.target.value)}>
                  <option value="">— Select —</option>
                  {machines.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
              </div>
              <div style={s.field}>
                <label style={s.label}>Mould</label>
                <div style={{ display: 'flex', gap: '6px' }}>
                  <select style={{ ...s.input, flex: 1 }} value={form.mould_id} onChange={e => set('mould_id', e.target.value)}>
                    <option value="">— Select —</option>
                    {moulds.map(m => <option key={m.id} value={m.id}>{m.ref} — {m.name}</option>)}
                  </select>
                  <button style={s.iconBtn} onClick={() => setShowMouldForm(v => !v)} title={showMouldForm ? 'Cancel' : 'Add new mould'}>
                    <i className={`fa-solid fa-${showMouldForm ? 'xmark' : 'plus'}`} />
                  </button>
                </div>
              </div>
            </div>
            {showMouldForm && (
              <div style={s.inlineForm}>
                <MouldForm compact onSaved={m => { setMoulds(ms => [...ms, m]); set('mould_id', m.id); setShowMouldForm(false); }} />
              </div>
            )}
            {selectedMould && (
              <div style={s.mouldHint}>
                <i className="fa-solid fa-circle-info" style={{ color: '#14b8a6', fontSize: '11px', flexShrink: 0 }} />
                <span>
                  {selectedMould.cavities ? `${selectedMould.cavities} cavities` : 'Cavities unknown'}
                  {selectedMould.sw_g  ? ` · ${selectedMould.sw_g}g shot`   : ''}
                  {selectedMould.pw_g  ? ` · ${selectedMould.pw_g}g/part`   : ''}
                </span>
              </div>
            )}

            <div style={s.field}>
              <label style={s.label}>Backup machines (priority order)</label>
              {secondaryIds.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '6px' }}>
                  {secondaryIds.map((id, idx) => (
                    <div key={id} style={s.machineRow}>
                      <span style={s.machineOrd}>{idx === 0 ? 'Secondary' : idx === 1 ? 'Tertiary' : `Backup ${idx + 1}`}</span>
                      <span style={{ flex: 1, fontSize: '13px' }}>{machineName(id)}</span>
                      <button style={s.miniBtn} disabled={idx === 0} onClick={() => moveSecondary(idx, -1)} title="Move up"><i className="fa-solid fa-chevron-up" /></button>
                      <button style={s.miniBtn} disabled={idx === secondaryIds.length - 1} onClick={() => moveSecondary(idx, 1)} title="Move down"><i className="fa-solid fa-chevron-down" /></button>
                      <button style={{ ...s.miniBtn, color: '#ef4444' }} onClick={() => removeSecondary(id)} title="Remove"><i className="fa-solid fa-xmark" /></button>
                    </div>
                  ))}
                </div>
              )}
              <select style={s.input} value="" onChange={e => addSecondary(e.target.value)}>
                <option value="">+ Add backup machine…</option>
                {availableMachines.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </div>

            <div style={s.row4}>
              <Field label="Cycle (s)" value={form.target_cycle_s} onChange={v => set('target_cycle_s', v)} type="number" step="0.1" />
              <div style={s.field}>
                <label style={s.label}>Shot wt (g)</label>
                <input style={s.input} type="number" value={form.shot_weight_g} onChange={e => set('shot_weight_g', e.target.value)} />
                {expectedShot && shotDiff > 1 && <div style={s.warnHint}>Expected ~{expectedShot}g</div>}
                {expectedShot && shotDiff !== null && shotDiff <= 1 && form.shot_weight_g !== '' && <div style={s.okHint}><i className="fa-solid fa-check" style={{ marginRight: '3px' }} />Matches</div>}
              </div>
              <Field label="Part wt (g)"  value={form.part_weight_g}  onChange={v => set('part_weight_g', v)}  type="number" />
              <Field label="Sprue wt (g)" value={form.sprue_weight_g} onChange={v => set('sprue_weight_g', v)} type="number" />
            </div>
            <div style={{ ...s.row4, gridTemplateColumns: 'minmax(0,1fr) minmax(0,3fr)' }}>
              <div style={s.field}>
                <label style={s.label}>Scrap %</label>
                <input style={s.input} type="number" value={form.scrap_pct} onChange={e => set('scrap_pct', e.target.value)} />
                <div style={s.hint}>Typical: 0.5–5%</div>
              </div>
            </div>
          </Section>

          {/* ── MATERIAL ── */}
          <Section title="Material" summary={sum.material}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <label style={s.label}>Approved recipes</label>
              <button style={s.iconBtn} onClick={() => setShowRecipeForm(v => !v)} title={showRecipeForm ? 'Cancel' : 'Add new recipe'}>
                <i className={`fa-solid fa-${showRecipeForm ? 'xmark' : 'plus'}`} />
              </button>
            </div>
            {/* Which recipes, and what is in each (29 Sep): "1 selected" alone
                said neither. */}
            <SelectedRecipes recipes={selectedRecipes} colours={colours}
              onRemove={id => set('recipe_ids', form.recipe_ids.filter(x => x !== id))} />
            <RecipeToggle>
              <RecipeSelector allRecipes={allRecipes} selectedIds={form.recipe_ids} colours={colours} onChange={ids => set('recipe_ids', ids)} />
            </RecipeToggle>
            {showRecipeForm && (
              <div style={s.inlineForm}>
                <RecipeForm onSaved={r => { setAllRecipes(rs => [...rs, r]); set('recipe_ids', [...form.recipe_ids, r.id]); setShowRecipeForm(false); }} />
              </div>
            )}
          </Section>

          {/* ── AT THE MACHINE ── */}
          <Section title="At the machine" summary={sum.atMachine}>
            <div style={s.row2}>
              {/* How much of a person this part needs while it runs (Staffing, 28 Sep). */}
              <div style={s.field}>
                <label style={s.label}>Attention while running</label>
                <select value={form.attention_level} onChange={e => set('attention_level', e.target.value)}
                  style={{ ...s.input, color: form.attention_level === '' ? '#f59e0b' : '#f0f2f5' }}>
                  <option value="">Not estimated</option>
                  <option value="1">1 · little to none (0–0.5 h a shift)</option>
                  <option value="2">2 · a little (0.5–1 h)</option>
                  <option value="3">3 · some (1–3 h)</option>
                  <option value="4">4 · a lot (3–6 h)</option>
                  <option value="5">5 · someone stays at the machine (a whole person)</option>
                </select>
                <div style={{ fontSize: '11px', color: '#6b7280', lineHeight: 1.45 }}>
                  How much of a person it needs over a shift. Suggestions for every part are on Admin → Staffing.
                </div>
              </div>
              {/* The box the parts drop into at the press -- not the final pack
                  (28 Sep). With the cycle time it says how often someone swaps it. */}
              <Field label="Collector box at the machine (pcs)" type="number" value={form.collector_box_qty} onChange={v => set('collector_box_qty', v)}
                hint="The box the parts drop into at the machine, e.g. 900. Not the final pack: that is under Packaging." />
            </div>
            <ReqField label="Handling"     presets={REQ_PRESETS.handling} value={form.handling_requirements} onChange={v => set('handling_requirements', v)} />
          </Section>

          {/* ── QUALITY ── */}
          <Section title="Quality" summary={sum.quality}>
            <div style={s.field}>
              <label style={s.label}>Classification</label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', padding: '4px 0' }}>
                <input type="checkbox" checked={form.is_critical} onChange={e => set('is_critical', e.target.checked)} style={{ accentColor: '#ef4444', width: '14px', height: '14px' }} />
                <span style={{ fontSize: '13px', color: form.is_critical ? '#fca5a5' : '#9ca3af', fontWeight: form.is_critical ? '600' : '400' }}>
                  {form.is_critical && <i className="fa-solid fa-triangle-exclamation" style={{ marginRight: '5px', color: '#ef4444', fontSize: '11px' }} />}
                  Critical / safety part
                </span>
              </label>
            </div>
            <ReqField label="Dimensional"  presets={REQ_PRESETS.dim}      value={form.dim_requirements}      onChange={v => set('dim_requirements', v)} />
            <ReqField label="Visual"       presets={REQ_PRESETS.visual}   value={form.visual_requirements}   onChange={v => set('visual_requirements', v)} />
            <Field label="Weight log interval (min)" value={form.weight_log_interval_minutes} onChange={v => set('weight_log_interval_minutes', v)} type="number" hint="How often operators log shot weight. Default: 60 min." />
          </Section>

          {/* ── PRODUCT & ASSEMBLY ── */}
          <Section title="Product & assembly" summary={sum.product}>
            {!showNewProduct ? (
              <>
                <div style={s.field}>
                  <label style={s.label}>Belongs to product</label>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <select style={{ ...s.input, flex: 1 }} value={productId} onChange={e => setProductId(e.target.value)}>
                      <option value="">— None —</option>
                      {products.map(p => <option key={p.id} value={p.id}>{p.code} — {p.name}</option>)}
                    </select>
                    <button style={s.iconBtn} onClick={() => setShowNewProduct(true)} title="Create new product">
                      <i className="fa-solid fa-plus" />
                    </button>
                  </div>
                </div>
                {productId && (
                  <Field label="Qty per assembly" value={String(productQty)} onChange={v => setProductQty(parseInt(v) || 1)} type="number" />
                )}
              </>
            ) : (
              <div style={s.inlineForm}>
                <div style={{ fontSize: '11px', fontWeight: '700', color: '#14b8a6', marginBottom: '8px' }}>New product</div>
                <div style={s.row2}>
                  <Field label="Product code *" value={newPCode} onChange={setNewPCode} />
                  <Field label="Product name *" value={newPName} onChange={setNewPName} />
                </div>
                <Field label="Qty per assembly" value={String(productQty)} onChange={v => setProductQty(parseInt(v) || 1)} type="number" />
                <button style={s.cancelBtn} onClick={() => { setShowNewProduct(false); setNewPCode(''); setNewPName(''); }}>Cancel</button>
              </div>
            )}
            <ReqField label="Assembly"     presets={REQ_PRESETS.assembly} value={form.assembly_requirements} onChange={v => set('assembly_requirements', v)} />
          </Section>

          {/* ── PACKAGING, or storage for a part that goes into a product ──
              The director, 29 Sep: parts that go into a product are not
              packed -- the product is -- they are kept in a crate or the
              collector box until assembly. */}
          <Section title={goesIntoProduct ? 'Stored until assembly' : 'Packaging'} summary={sum.packaging}>
            {goesIntoProduct && (
              <div style={{ fontSize: '12px', color: '#9ca3af', lineHeight: 1.5, marginBottom: '12px' }}>
                This part goes into a product, so it is not packed: the product is, on the product itself (Admin → Parts → the product's pencil). Say here how the part is kept until assembly, for example a crate or the collector box.
              </div>
            )}
            {!canEditPackaging && (
              <div style={s.readOnlyNote}>
                <i className="fa-solid fa-lock" style={{ marginRight: '6px' }} />
                Only directors can change packaging values.
              </div>
            )}
            <PackagingLayersEditor layers={form.packaging_layers} containers={containers} disabled={!canEditPackaging}
              label={goesIntoProduct ? 'Stored in — from the part outwards' : undefined}
              onChange={layers => set('packaging_layers', layers)} onAddContainer={addContainer} />
            {!goesIntoProduct && <div style={s.row2}>
              <div style={s.field}>
                <label style={s.label}>How the count is checked</label>
                <select style={s.input} value={form.packing_count_method} disabled={!canEditPackaging} onChange={e => set('packing_count_method', e.target.value)}>
                  <option value="">Not said</option>
                  {Object.entries(COUNT_WORDS).map(([k, w]) => <option key={k} value={k}>{w}</option>)}
                </select>
              </div>
              <div style={s.field}>
                <label style={s.label}>Where it is packed</label>
                <select style={s.input} value={form.packing_place} disabled={!canEditPackaging} onChange={e => set('packing_place', e.target.value)}>
                  <option value="">Not said</option>
                  {Object.entries(PACK_PLACE_WORDS).map(([k, w]) => <option key={k} value={k}>{w}</option>)}
                </select>
              </div>
            </div>}
            {!goesIntoProduct && <WrappingEditor form={form} set={set} containers={containers} disabled={!canEditPackaging} />}
            <Field label="Packaging notes" value={form.packaging_requirements} onChange={v => set('packaging_requirements', v)} textarea />
          </Section>

          {/* ── NOTES ── */}
          <Section title="Notes" summary={sum.notes}>
            <Field label="Notes" value={form.description} onChange={v => set('description', v)} textarea hint="General remarks, additional context, or internal notes about this part." />
          </Section>
          </>
          )}
        </div>
        <div style={s.modalFooter}>
          <button style={s.cancelBtn} onClick={onClose}>Cancel</button>
          <button style={{ ...s.addBtn, opacity: saving ? 0.6 : 1 }} disabled={saving || !loaded} onClick={save}>
            {saving ? 'Saving...' : part ? 'Save Changes' : 'Add Part'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── CHIP INPUT (requirements) ────────────────────────────────
function ReqField({ label, presets, value, onChange }) {
  const [customText, setCustomText] = useState('');
  const customChips = value.filter(c => !presets.includes(c));

  function toggle(p) { onChange(value.includes(p) ? value.filter(x => x !== p) : [...value, p]); }
  function addCustom() {
    const t = customText.trim();
    if (!t || value.includes(t)) return;
    onChange([...value, t]); setCustomText('');
  }

  return (
    <div style={{ ...s.field, marginBottom: '18px' }}>
      <label style={s.label}>{label}</label>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: customChips.length ? '8px' : '6px' }}>
        {presets.map(p => (
          <button key={p} type="button" onClick={() => toggle(p)} style={{
            background: value.includes(p) ? '#0f766e' : '#0f1117',
            color: value.includes(p) ? '#5eead4' : '#9ca3af',
            border: `1px solid ${value.includes(p) ? '#0d9488' : '#2a3040'}`,
            borderRadius: '20px', padding: '5px 12px', fontSize: '11px', cursor: 'pointer',
            fontWeight: value.includes(p) ? '600' : '400',
          }}>
            {value.includes(p) && <i className="fa-solid fa-check" style={{ marginRight: '4px', fontSize: '9px' }} />}
            {p}
          </button>
        ))}
      </div>
      {customChips.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' }}>
          {customChips.map(c => (
            <span key={c} style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', background: '#1a2535', border: '1px solid #2a3040', borderRadius: '20px', padding: '5px 12px', fontSize: '11px', color: '#f0f2f5' }}>
              {c}
              <button type="button" onClick={() => onChange(value.filter(x => x !== c))} style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', padding: 0, fontSize: '13px', lineHeight: 1 }}>×</button>
            </span>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', gap: '6px' }}>
        <input value={customText} onChange={e => setCustomText(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCustom(); } }}
          placeholder="Add custom requirement…"
          style={{ ...s.input, flex: 1, fontSize: '12px', padding: '6px 10px' }} />
        {customText.trim() && (
          <button type="button" onClick={addCustom} style={{ background: '#0f766e', border: 'none', color: '#fff', borderRadius: '6px', padding: '6px 14px', fontSize: '12px', cursor: 'pointer', flexShrink: 0 }}>Add</button>
        )}
      </div>
    </div>
  );
}

// ── RECIPE TOGGLE WRAPPER ────────────────────────────────────
function RecipeToggle({ children }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button onClick={() => setOpen(v => !v)}
        style={{ background: 'none', border: `1px solid #2a3040`, borderRadius: '6px', color: '#9ca3af', fontSize: '12px', cursor: 'pointer', padding: '6px 12px', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: open ? '10px' : '0', width: '100%' }}>
        <i className={`fa-solid fa-chevron-${open ? 'up' : 'down'}`} style={{ fontSize: '9px', color: '#6b7280' }} />
        {open ? 'Hide recipe list' : 'Browse & select recipes'}
      </button>
      {open && children}
    </div>
  );
}

// ── RECIPE SELECTOR ──────────────────────────────────────────
function RecipeSelector({ allRecipes, selectedIds, colours, onChange }) {
  const [search, setSearch]             = useState('');
  const [matFilter, setMatFilter]       = useState('');
  const [colourFilter, setColourFilter] = useState('');
  const [expanded, setExpanded]         = useState(null);

  const baseMaterials = [...new Set(allRecipes.map(r => r.material?.name).filter(Boolean))].sort();
  const filtered = allRecipes.filter(r => {
    if (search) { const q = search.toLowerCase(); if (!r.name.toLowerCase().includes(q) && !(r.material?.name || '').toLowerCase().includes(q)) return false; }
    if (matFilter && r.material?.name !== matFilter) return false;
    if (colourFilter) { const hasMB = (r.recipe_components || []).some(c => c.type === 'masterbatch' && c.material?.colour === colourFilter); if (!hasMB) return false; }
    return true;
  });

  return (
    <div>
      <div style={rs.filterBar}>
        <input style={rs.search} placeholder="Search recipes..." value={search} onChange={e => setSearch(e.target.value)} />
        <select style={rs.filter} value={matFilter} onChange={e => setMatFilter(e.target.value)}>
          <option value="">All materials</option>
          {baseMaterials.map(m => <option key={m} value={m}>{m}</option>)}
        </select>
        <select style={rs.filter} value={colourFilter} onChange={e => setColourFilter(e.target.value)}>
          <option value="">All colours</option>
          {colours.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
        </select>
      </div>
      {allRecipes.length === 0 ? (
        <div style={{ fontSize: '12px', color: '#6b7280' }}>No recipes found — create one below</div>
      ) : filtered.length === 0 ? (
        <div style={{ fontSize: '12px', color: '#6b7280', padding: '6px 0' }}>No recipes match filters</div>
      ) : filtered.map(r => {
        const isSelected   = selectedIds.includes(r.id);
        const isOpen       = expanded === r.id;
        const mbWithColour = (r.recipe_components || []).find(c => c.type === 'masterbatch' && c.material?.colour);
        const dotHex       = mbWithColour ? (colours.find(c => c.name === mbWithColour.material.colour)?.hex || null) : null;
        return (
          <div key={r.id} style={{ ...rs.item, ...(isSelected ? rs.itemSelected : {}) }}>
            <div style={rs.itemRow}>
              <input type="checkbox" checked={isSelected} onChange={e => onChange(e.target.checked ? [...selectedIds, r.id] : selectedIds.filter(id => id !== r.id))} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  {dotHex && <div style={{ width: '9px', height: '9px', borderRadius: '50%', background: dotHex, flexShrink: 0 }} />}
                  <span style={rs.recipeName}>{r.name}</span>
                </div>
                <span style={rs.recipeMat}>{r.material?.name}</span>
              </div>
              <button style={rs.expandBtn} onClick={() => setExpanded(isOpen ? null : r.id)}>{isOpen ? '▴' : '▾'}</button>
            </div>
            {isOpen && (
              <div style={rs.components}>
                <div style={rs.compRow}><span style={rs.compBase}>Base</span><span style={rs.compName}>{r.material?.name}</span></div>
                {(r.recipe_components || []).map(c => {
                  const cHex = c.type === 'masterbatch' && c.material?.colour ? (colours.find(cl => cl.name === c.material.colour)?.hex || null) : null;
                  return (
                    <div key={c.id} style={rs.compRow}>
                      <span style={{ ...rs.compBadge, background: c.type === 'masterbatch' ? '#1d4ed8' : c.type === 'base' ? '#0f766e' : '#166534' }}>
                        {c.type === 'masterbatch' ? 'MB' : c.type === 'base' ? 'BASE' : 'ADD'}
                      </span>
                      <span style={rs.compName}>
                        {cHex && <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', background: cHex, marginRight: '4px', verticalAlign: 'middle' }} />}
                        {c.material?.name}
                      </span>
                      <span style={rs.compPct}>{c.percentage}%</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── PACKAGING LAYERS ─────────────────────────────────────────
// How a part is packed, from the part outwards (29 Sep, for the assembly and
// packaging processes to come): what it goes into, how many, and how they are
// placed. The first layer counts parts; each other layer counts the one inside
// it. "Other…" types a new container, which joins the list for everyone.
export const PLACEMENT_WORDS = { loose: 'Loose (poured in)', layers: 'Arranged in layers', stacked: 'Stacked or nested', dividers: 'In a tray or dividers' };
export const COUNT_WORDS = { hand: 'Counted by hand', weighed: 'Weighed', machine_counter: "The machine's counter" };
export const PACK_PLACE_WORDS = { machine: 'At the machine', packing_table: 'At a packing table' };

// Parts in each layer: [4, 96, 2880] for box x4 -> transport box x24 -> pallet x30.
export function partsPerLayer(layers) {
  let n = 1;
  return (layers || []).map(l => (n = n != null && l.qty ? n * l.qty : null));
}

// One line: "Plastic bag 1 → Product box ×1 → Transport box ×15 → Pallet ×30".
export function packagingChain(layers, containers) {
  return (layers || []).map((l, i) => `${containers.find(c => c.id === Number(l.container_id))?.name || '?'} ${i === 0 ? (l.qty != null ? Number(l.qty).toLocaleString('en-GB') : '?') : `×${l.qty ?? '?'}`}`).join(' → ');
}

// Where an extra goes in its layer (15 Oct 2026): a cardboard cover on top of
// each pallet, slip sheets between the layers, an insert in each box.
export const EXTRA_WHERE_WORDS = { top: 'on top of each', bottom: 'under each', inside: 'inside each', between: 'between the layers', every_layer: 'in every layer' };
// An extra's place in words. On a pallet, "on top of each euro pallet" read as
// on the bare boards: a cover goes on top of the load, the top tray when the
// pallet takes one a layer (the director, 29 Oct 2026). Counted the same.
export function extraWhereWords(where, layers, i, containers) {
  const own = byIdIn(containers, layers[i]?.container_id);
  const inner = i > 0 ? (byIdIn(containers, layers[i - 1].container_id)?.name || '').toLowerCase() : '';
  if (own?.category === 'pallet' && inner && (where === 'top' || where === 'bottom')) {
    const one = Number(layers[i].per_layer) === 1;
    if (where === 'top') return one ? `on top of the top ${inner}` : 'on top of the load';
    return one ? `on the pallet, under the bottom ${inner}` : 'on the pallet, under the load';
  }
  const words = EXTRA_WHERE_WORDS[where] || '';
  return ['top', 'bottom', 'inside'].includes(where) ? `${words} ${(own?.name || 'one').toLowerCase()}` : words;
}
export const STACK_WORDS = { none: 'Does not stack', carries: 'Can carry one pallet on top', on_top: 'Can go on top of another pallet', both: 'Can carry one, and go on top of another (like Dose)' };

// The same arithmetic as the server's (utils/weights.js): a transport box
// stands its inside height plus its board top and bottom; a full pallet is the
// pallet + its layers x one layer's height -- typed on the layer where the
// contents stand taller than their container (Dose above its tray), else the
// container's. { mm, from: 'layers' | 'typed' | null, pi } -- null with no pallet layer.
const WALL_MM = { '3_layer': 4, '5_layer': 7, micro_flute: 2 };
const byIdIn = (containers, id) => containers.find(c => c.id === Number(id));
export function standingHeight(c) {
  if (!c || !(Number(c.height_mm) > 0)) return null;
  return Number(c.height_mm) + (c.category === 'transport_box' ? 2 * (WALL_MM[c.board] || 0) : 0);
}
export const layersHigh = (l) => (l.per_layer && l.qty ? Math.ceil(l.qty / l.per_layer) : (l.layer_count || null));
export function palletHeightOf(layers, containers, typed) {
  const pi = (layers || []).findIndex(l => byIdIn(containers, l.container_id)?.category === 'pallet');
  if (pi < 0) return null;
  const l = layers[pi];
  const P = byIdIn(containers, l.container_id);
  const inner = pi > 0 ? byIdIn(containers, layers[pi - 1].container_id) : null;
  const count = layersHigh(l);
  const one = Number(l.layer_height_mm) > 0 ? Number(l.layer_height_mm) : standingHeight(inner);
  if (P && Number(P.height_mm) > 0 && count && one) return { mm: Number(P.height_mm) + count * one, from: 'layers', pi };
  if (Number(typed) > 0) return { mm: Number(typed), from: 'typed', pi };
  return { mm: null, from: null, pi };
}

// Why the full pallet height cannot be worked out from the layers, in words
// (20 Oct 2026), or null when it can. Says what to fill in, and where.
export function palletHeightGap(layers, containers) {
  const pi = (layers || []).findIndex(l => byIdIn(containers, l.container_id)?.category === 'pallet');
  if (pi < 0) return 'there is no pallet layer';
  const l = layers[pi];
  const P = byIdIn(containers, l.container_id);
  const inner = pi > 0 ? byIdIn(containers, layers[pi - 1].container_id) : null;
  const need = [];
  if (!layersHigh(l)) need.push('how many stand in one layer (e.g. 2 × 4) on the pallet layer');
  if (!(Number(l.layer_height_mm) > 0) && !standingHeight(inner)) need.push(`how tall one layer is (${inner ? `${inner.name} has no height in Admin → Packaging` : 'nothing stands on the pallet yet'})`);
  if (!(P && Number(P.height_mm) > 0)) need.push(`the height of ${P ? P.name : 'the pallet'} in Admin → Packaging`);
  return need.length ? `needs ${need.join(', and ')}` : null;
}

// How a pallet's boxes stand, on its layer: the pattern (2 x 4 is 8 a layer),
// how many a layer, how many layers high, and one layer's height where the
// contents stand taller than their box. Whatever can be worked out is.
function PalletStanding({ layer, inner, setLayer, disabled }) {
  const pat = Array.isArray(layer.pattern) ? layer.pattern : [null, null];
  const num = (v) => (v === '' || v == null ? null : Number(v));
  const small = { ...s.input, width: '58px', padding: '6px 8px', textAlign: 'right' };
  function setPattern(i, v) {
    const p = [...pat]; p[i] = num(v);
    const both = p[0] > 0 && p[1] > 0;
    setLayer({ pattern: p[0] == null && p[1] == null ? null : p, ...(both ? { per_layer: p[0] * p[1] } : {}) });
  }
  function setCount(v) {
    const n = num(v);
    // No box count yet: it is filled in from per layer x layers.
    setLayer({ layer_count: n, ...(layer.per_layer && n && !layer.qty ? { qty: layer.per_layer * n } : {}) });
  }
  const high = layersHigh(layer);
  const innerH = standingHeight(inner);
  return (
    <div style={{ marginTop: '6px', marginLeft: '24px', display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap', fontSize: '12px', color: '#9ca3af' }}>
      <span>On the pallet:</span>
      <input type="number" min="1" value={pat[0] ?? ''} disabled={disabled} placeholder="" onChange={e => setPattern(0, e.target.value)} style={small} title="Pattern: across, e.g. 2" />
      <span>×</span>
      <input type="number" min="1" value={pat[1] ?? ''} disabled={disabled} placeholder="" onChange={e => setPattern(1, e.target.value)} style={small} title="Pattern: along, e.g. 4" />
      <span>=</span>
      <input type="number" min="1" value={layer.per_layer ?? ''} disabled={disabled} placeholder="" onChange={e => setLayer({ per_layer: num(e.target.value) })} style={small} title="How many in one layer (type it where the pattern is not a grid)" />
      <span>a layer ·</span>
      {layer.qty && layer.per_layer
        ? <span style={{ color: '#f0f2f5' }}>{high} layer{high === 1 ? '' : 's'} high</span>
        : <><input type="number" min="1" value={layer.layer_count ?? ''} disabled={disabled} placeholder="" onChange={e => setCount(e.target.value)} style={small} title="Layers high" /><span>layers high</span>
          {!high && <span style={{ color: '#f59e0b' }}>← how many a layer: the layers and the pallet height then follow</span>}</>}
      <span>·</span>
      <input type="number" min="1" value={layer.layer_height_mm ?? ''} disabled={disabled} placeholder={innerH ? String(innerH) : 'mm'}
        onChange={e => setLayer({ layer_height_mm: num(e.target.value) })} style={{ ...small, width: '68px' }}
        title={innerH ? `One layer's height. Empty: the box's, ${innerH} mm. Type it where what is inside stands taller than the box.` : "One layer's height, mm"} />
      <span>mm a layer{!layer.layer_height_mm && innerH ? ' (the box)' : ''}</span>
      {/* Small orders with no pallet of their own (the suction cup holders):
          only full pallets count; what is left rides on another pallet. */}
      <label style={{ flexBasis: '100%', display: 'flex', gap: '6px', alignItems: 'center', color: '#d1d5db', marginTop: '2px' }}>
        <input type="checkbox" checked={layer.part_rides === true} disabled={disabled}
          onChange={e => setLayer({ part_rides: e.target.checked || undefined })} />
        What doesn't fill a pallet rides on top of another pallet <span style={{ color: '#6b7280' }}>(no pallet of its own: only full pallets count)</span>
      </label>
    </div>
  );
}

// A layer's extras: trays, sheets, inserts, top plates from the catalogue,
// how many and where.
function LayerExtras({ layer, containers, setLayer, disabled, words }) {
  const extras = layer.extras || [];
  const setExtra = (k, patch) => setLayer({ extras: extras.map((e, j) => (j === k ? { ...e, ...patch } : e)) });
  const inserts = containers.filter(c => c.category === 'insert');
  const rest = containers.filter(c => c.category !== 'insert');
  if (!extras.length && disabled) return null;
  return (
    <div style={{ marginTop: '6px', marginLeft: '24px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
      {extras.map((e, k) => (
        <div key={k} style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap', fontSize: '12px', color: '#9ca3af' }}>
          <span>+</span>
          <input type="number" min="1" value={e.qty ?? ''} disabled={disabled} placeholder="1"
            onChange={ev => setExtra(k, { qty: ev.target.value === '' ? null : Number(ev.target.value) })}
            style={{ ...s.input, width: '58px', padding: '6px 8px', textAlign: 'right' }} />
          <select value={e.container_id ?? ''} disabled={disabled} onChange={ev => setExtra(k, { container_id: ev.target.value === '' ? null : Number(ev.target.value) })}
            style={{ ...s.input, width: 'auto', minWidth: '170px', padding: '6px 8px', color: e.container_id == null ? '#f59e0b' : '#f0f2f5' }}>
            <option value="">Which extra…</option>
            {inserts.length > 0 && <optgroup label="Trays, inserts & sheets">{inserts.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</optgroup>}
            <optgroup label="Everything else">{rest.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</optgroup>
          </select>
          <select value={e.where || 'top'} disabled={disabled} onChange={ev => setExtra(k, { where: ev.target.value })}
            style={{ ...s.input, width: 'auto', padding: '6px 8px' }}>
            {Object.keys(EXTRA_WHERE_WORDS).map(w => <option key={w} value={w}>{words(w)}</option>)}
          </select>
          {!disabled && (
            <button type="button" title="Take this extra off" onClick={() => setLayer({ extras: extras.filter((_, j) => j !== k) })}
              style={{ ...s.miniBtn, color: '#9ca3af' }}><i className="fa-solid fa-xmark" /></button>
          )}
        </div>
      ))}
      {!disabled && extras.length < 5 && (
        <button type="button" onClick={() => setLayer({ extras: [...extras, { container_id: null, qty: 1, where: 'top' }] })}
          style={{ background: 'none', border: 'none', color: '#14b8a6', fontSize: '11.5px', cursor: 'pointer', padding: 0, alignSelf: 'flex-start' }}>
          + extra (tray, sheet, insert, top plate…)
        </button>
      )}
    </div>
  );
}

// How the pallets go on the lorry and are wrapped (15 Oct 2026, 074), below
// the layers: used by the part form, the product form and the Packaging page.
// `form` holds the item's fields; `set(key, value)` changes one.
export function WrappingEditor({ form, set, containers, disabled }) {
  const layers = form.packaging_layers || [];
  const h = palletHeightOf(layers, containers, form.pallet_height_mm);
  const hasBox = layers.some(l => byIdIn(containers, l.container_id)?.category === 'transport_box');
  const stacking = form.pallet_stacking || 'none';
  const intIn = (k, label, max, placeholder) => (
    <div style={s.field}>
      <label style={s.label}>{label}</label>
      <input type="number" min="0" max={max} style={s.input} value={form[k] ?? ''} disabled={disabled} placeholder={placeholder}
        onChange={e => set(k, e.target.value === '' ? null : Number(e.target.value))} />
    </div>
  );
  return (
    <div style={{ ...s.field, borderTop: '1px solid #2a3040', paddingTop: '10px', marginTop: '4px' }}>
      <label style={s.label}>On the lorry and wrapping</label>
      {!h && <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '6px' }}>Give the packaging a pallet layer (a pallet from Admin → Packaging) to say how its pallets stack and are wrapped.</div>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0 12px' }}>
        <div style={s.field}>
          <label style={s.label}>Pallets on the lorry</label>
          <select style={s.input} value={stacking} disabled={disabled} onChange={e => set('pallet_stacking', e.target.value)}>
            {Object.entries(STACK_WORDS).map(([k, w]) => <option key={k} value={k}>{w}</option>)}
          </select>
        </div>
        <div style={s.field}>
          <label style={s.label}>Wrapping</label>
          <select style={s.input} value={form.wrap_direction || ''} disabled={disabled} onChange={e => set('wrap_direction', e.target.value || null)}>
            <option value="">Not said</option>
            <option value="up">Up only</option>
            <option value="up_down">Up and down</option>
          </select>
        </div>
        {intIn('wrap_bottom_turns', 'Turns at the bottom', 20, '2')}
        {intIn('wrap_top_turns', 'Turns at the top', 20, '1')}
        {intIn('wrap_rotation_speed', 'Rotation speed (0–10)', 10, '5')}
        {intIn('wrap_lift_speed', 'Lift speed (0–10)', 10, '6')}
        {h && h.from === 'layers'
          ? <div style={s.field}><label style={s.label}>Full pallet height</label><div style={{ fontSize: '13px', color: '#f0f2f5', padding: '8px 0' }}>{h.mm.toLocaleString('en-GB')} mm <span style={{ color: '#6b7280', fontSize: '11px' }}>from the layers</span></div></div>
          : intIn('pallet_height_mm', 'Full pallet height (mm)', 2700, 'or type it')}
      </div>
      {h && h.from !== 'layers' && palletHeightGap(layers, containers) && (
        <div style={{ fontSize: '12px', color: '#f59e0b', marginTop: '2px' }}>
          The full pallet height {h.from === 'typed' ? 'is as typed: working it out from the layers' : 'cannot be worked out yet: it'} {palletHeightGap(layers, containers)}.
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '4px', fontSize: '12.5px', color: '#d1d5db' }}>
        {(stacking === 'carries' || stacking === 'both') && (
          <label style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <input type="checkbox" checked={form.cover_when_carrying !== false} disabled={disabled} onChange={e => set('cover_when_carrying', e.target.checked)} />
            Gets a cover sheet even with a pallet on top <span style={{ color: '#6b7280', fontSize: '11px' }}>(Kappe yes; Dose under Dose no)</span>
          </label>
        )}
        {hasBox && (
          <label style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <input type="checkbox" checked={!!form.box_extra_tape} disabled={disabled} onChange={e => set('box_extra_tape', e.target.checked)} />
            Its transport boxes get extra tape, for strength <span style={{ color: '#6b7280', fontSize: '11px' }}>(4 strips across the width too, as on KiSi and Kappe)</span>
          </label>
        )}
      </div>
    </div>
  );
}

// The wrapping fields as a form starts them, and as they are sent.
export const WRAP_KEYS = ['pallet_stacking', 'wrap_direction', 'wrap_bottom_turns', 'wrap_top_turns', 'wrap_rotation_speed', 'wrap_lift_speed', 'pallet_height_mm', 'cover_when_carrying', 'box_extra_tape'];
export function wrappingOf(item) {
  return {
    pallet_stacking: item?.pallet_stacking || 'none', wrap_direction: item?.wrap_direction || null,
    wrap_bottom_turns: item?.wrap_bottom_turns ?? null, wrap_top_turns: item?.wrap_top_turns ?? null,
    wrap_rotation_speed: item?.wrap_rotation_speed ?? null, wrap_lift_speed: item?.wrap_lift_speed ?? null,
    pallet_height_mm: item?.pallet_height_mm ?? null,
    cover_when_carrying: item?.cover_when_carrying !== false, box_extra_tape: !!item?.box_extra_tape,
  };
}

// A new layer starts with the default pallet (the Euro pallet, 99.9% of the
// time -- the director, 15 Oct 2026) while the packaging has no pallet yet.
function newLayerContainer(layers, containers) {
  if (!layers.length || layers.some(l => byIdIn(containers, l.container_id)?.category === 'pallet')) return null;
  const d = containers.find(c => c.category === 'pallet' && c.is_default);
  return d ? d.id : null;
}

// Used by the part form and the product form (AdminParts).
export function PackagingLayersEditor({ layers, containers, onChange, onAddContainer, disabled, label = 'Packed as — from the part outwards', unit = 'parts' }) {
  const [other, setOther] = useState(null);   // { index, text, busy, error } while typing a new one
  const nameOf = id => containers.find(c => c.id === Number(id))?.name;
  const per = partsPerLayer(layers);
  const setLayer = (i, patch) => onChange(layers.map((l, j) => (j === i ? { ...l, ...patch } : l)));

  function pick(i, v) {
    if (v === '__other') { setOther({ index: i, text: '', busy: false, error: null }); return; }
    setOther(null);
    setLayer(i, { container_id: v === '' ? null : Number(v) });
  }
  function addOther() {
    const text = (other?.text || '').trim();
    if (!text || other.busy) return;
    const index = other.index;
    setOther(o => ({ ...o, busy: true, error: null }));
    onAddContainer(text)
      .then(c => { setLayer(index, { container_id: c.id }); setOther(null); })
      .catch(e => setOther(o => ({ ...o, busy: false, error: e.response?.data?.error || 'That could not be added just now.' })));
  }

  return (
    <div style={s.field}>
      <label style={s.label}>{label}</label>
      {layers.length === 0 && <div style={{ fontSize: '12px', color: '#6b7280' }}>No packaging entered yet.</div>}
      {layers.map((l, i) => {
        const typing = other && other.index === i;
        return (
          <div key={i} style={{ background: '#0f1117', border: '1px solid #2a3040', borderRadius: '8px', padding: '8px 10px' }}>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ width: '16px', fontSize: '11px', color: '#6b7280', fontWeight: 700 }}>{i + 1}</span>
              <select value={typing ? '__other' : (l.container_id ?? '')} disabled={disabled} onChange={e => pick(i, e.target.value)}
                style={{ ...s.input, flex: '2 1 150px', minWidth: 0, width: 'auto', color: l.container_id == null && !typing ? '#f59e0b' : '#f0f2f5' }}>
                <option value="">What it goes into…</option>
                {containers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                <option value="__other">Other…</option>
              </select>
              <input type="number" min="1" value={l.qty ?? ''} disabled={disabled} placeholder="how many"
                onChange={e => setLayer(i, { qty: e.target.value === '' ? null : Number(e.target.value) })}
                style={{ ...s.input, flex: '0 0 92px', width: '92px', textAlign: 'right' }} />
              <span style={{ flex: '1 1 70px', minWidth: 0, fontSize: '12px', color: '#9ca3af' }}>
                {i === 0 ? unit : `× ${nameOf(layers[i - 1].container_id) || 'the layer above'}`}
                {i > 0 && per[i] ? <span style={{ color: '#6b7280' }}> · {per[i].toLocaleString('en-GB')} {unit}</span> : null}
              </span>
              {!disabled && (
                <button type="button" onClick={() => { setOther(null); onChange(layers.filter((_, j) => j !== i)); }} title="Remove this layer"
                  style={{ ...s.miniBtn, color: '#9ca3af' }}><i className="fa-solid fa-xmark" /></button>
              )}
            </div>
            <div style={{ marginTop: '6px', marginLeft: '24px' }}>
              <select value={l.placement || ''} disabled={disabled} onChange={e => setLayer(i, { placement: e.target.value || null })}
                style={{ ...s.input, width: 'auto', minWidth: '220px', maxWidth: '100%' }}>
                <option value="">How placed…</option>
                {Object.entries(PLACEMENT_WORDS).map(([k, w]) => <option key={k} value={k}>{w}</option>)}
              </select>
            </div>
            {byIdIn(containers, l.container_id)?.category === 'pallet' && (
              <PalletStanding layer={l} inner={i > 0 ? byIdIn(containers, layers[i - 1].container_id) : null}
                setLayer={patch => setLayer(i, patch)} disabled={disabled} />
            )}
            <LayerExtras layer={l} containers={containers} setLayer={patch => setLayer(i, patch)} disabled={disabled}
              words={w => extraWhereWords(w, layers, i, containers)} />
            {typing && (
              <div style={{ marginTop: '8px', marginLeft: '24px' }}>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input autoFocus value={other.text} placeholder="What do they go into? e.g. Cardboard tray"
                    onChange={e => { const text = e.target.value; setOther(o => ({ ...o, text, error: null })); }}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addOther(); } if (e.key === 'Escape') setOther(null); }}
                    style={{ ...s.input, flex: 1 }} />
                  <button type="button" onClick={addOther} disabled={!other.text.trim() || other.busy}
                    style={{ ...s.addBtn, padding: '8px 14px', opacity: !other.text.trim() || other.busy ? 0.5 : 1 }}>{other.busy ? 'Adding…' : 'Add'}</button>
                  <button type="button" onClick={() => setOther(null)} style={{ ...s.cancelBtn, padding: '8px 12px' }}>Cancel</button>
                </div>
                <div style={{ fontSize: '11px', color: other.error ? '#fca5a5' : '#6b7280', marginTop: '4px' }}>
                  {other.error || 'Added to the list for everyone, so next time it is a normal choice.'}
                </div>
              </div>
            )}
          </div>
        );
      })}
      {!disabled && layers.length < 6 && (
        <button type="button" onClick={() => onChange([...layers, { container_id: newLayerContainer(layers, containers), qty: null, placement: null }])}
          style={{ ...s.cancelBtn, padding: '6px 12px', fontSize: '12px', alignSelf: 'flex-start' }}>
          + add layer
        </button>
      )}
    </div>
  );
}

// The same layers, to read: on the part's detail page.
export function PackagingLayersView({ layers, containers }) {
  if (!layers || !layers.length) return <div style={{ fontSize: '13px', color: '#4b5563', padding: '4px 0 8px' }}>No packaging entered yet.</div>;
  const nameOf = id => containers.find(c => c.id === Number(id))?.name;
  const per = partsPerLayer(layers);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '8px' }}>
      {layers.map((l, i) => {
        const name = nameOf(l.container_id);
        const how = [
          l.qty ? (i === 0 ? `${l.qty.toLocaleString('en-GB')} parts` : `${l.qty.toLocaleString('en-GB')} × ${nameOf(layers[i - 1].container_id) || 'the layer above'}`) : 'how many not set',
          l.placement && PLACEMENT_WORDS[l.placement].toLowerCase(),
          l.per_layer && `${Array.isArray(l.pattern) && l.pattern[0] && l.pattern[1] ? `${l.pattern[0]} × ${l.pattern[1]} = ` : ''}${l.per_layer} a layer, ${layersHigh(l)} high`,
          l.part_rides && 'a part pallet rides on another',
          ...(l.extras || []).map(e => `+ ${e.qty} ${nameOf(e.container_id) || '?'} ${extraWhereWords(e.where, layers, i, containers)}`),
        ].filter(Boolean).join(' · ');
        return (
          <div key={i} style={{ display: 'flex', gap: '10px', alignItems: 'center', background: '#0f1117', border: '1px solid #2a3040', borderRadius: '8px', padding: '8px 10px' }}>
            <span style={{ width: '14px', fontSize: '11px', color: '#6b7280', fontWeight: 700 }}>{i + 1}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '13px', fontWeight: 600, color: name ? '#f0f2f5' : '#f59e0b' }}>{name || 'Container not set'}</div>
              <div style={{ fontSize: '11.5px', color: '#9ca3af' }}>{how}</div>
            </div>
            {i > 0 && per[i] && <span style={{ fontSize: '12px', color: '#6b7280', whiteSpace: 'nowrap' }}>{per[i].toLocaleString('en-GB')} parts</span>}
          </div>
        );
      })}
    </div>
  );
}

// ── SELECTED RECIPES ─────────────────────────────────────────
// The recipes this part runs with, spelled out (29 Sep): every base material,
// masterbatch and additive with its share, so nobody has to open the recipe
// list to learn what "1 selected" means.
const RECIPE_GROUPS = [
  ['base',        'Base material', 'Base materials'],
  ['masterbatch', 'Masterbatch',   'Masterbatches'],
  ['additive',    'Additive',      'Additives'],
];
// Shared with the part card (PartDetail), which shows it without the remove
// button and with its own line for a part that has no recipe.
export function SelectedRecipes({ recipes, colours, onRemove, emptyText }) {
  if (!recipes.length) {
    return (
      <div style={{ fontSize: '12px', color: '#f59e0b', background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.25)', borderRadius: '8px', padding: '9px 12px', marginBottom: '10px' }}>
        {emptyText || 'No recipe yet. Pick one from the list below, or add a new one with +.'}
      </div>
    );
  }
  const hexOf = c => (c.material?.colour ? colours.find(cl => cl.name === c.material.colour)?.hex || null : null);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '10px' }}>
      {recipes.map((r, i) => {
        const comps = r.recipe_components || [];
        // A recipe whose base lives only on the recipe itself still shows it.
        const all = comps.some(c => c.type === 'base') ? comps
          : [{ id: 'base', type: 'base', percentage: 100, material: r.material }, ...comps];
        const groupOf = c => (c.type === 'base' || c.type === 'masterbatch' ? c.type : 'additive');
        const mb = all.find(c => c.type === 'masterbatch' && hexOf(c));
        return (
          <div key={r.id} style={{ background: '#0f1117', border: '1px solid #0d9488', borderRadius: '8px', padding: '10px 12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '8px' }}>
              {mb && <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: hexOf(mb), flexShrink: 0 }} />}
              <span style={{ fontSize: '13px', fontWeight: '700', color: '#f0f2f5', flex: 1, minWidth: 0 }}>{r.name}</span>
              {recipes.length > 1 && i === 0 && (
                <span style={{ fontSize: '9px', fontWeight: '700', color: '#5eead4', background: '#0f766e', borderRadius: '4px', padding: '1px 6px' }}>PRIMARY</span>
              )}
              {onRemove && <button type="button" onClick={() => onRemove(r.id)} title="Take this recipe off the part"
                style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: '13px', padding: '0 2px' }}>
                <i className="fa-solid fa-xmark" />
              </button>}
            </div>
            {RECIPE_GROUPS.map(([type, one, many]) => {
              const list = all.filter(c => groupOf(c) === type).sort((a, b) => Number(b.percentage) - Number(a.percentage));
              return (
                <div key={type} style={{ display: 'flex', gap: '10px', fontSize: '12px', lineHeight: 1.6 }}>
                  <span style={{ width: '104px', flexShrink: 0, color: '#6b7280' }}>{list.length === 1 ? one : many}</span>
                  <span style={{ flex: 1, minWidth: 0, color: list.length ? '#d1d5db' : '#4b5563' }}>
                    {list.length ? list.map((c, j) => (
                      <span key={c.id || j} style={{ display: 'block' }}>
                        {hexOf(c) && <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', background: hexOf(c), marginRight: '5px', verticalAlign: 'middle' }} />}
                        {c.material?.name || 'unnamed'} <b style={{ color: '#5eead4', fontWeight: '600' }}>{Number(c.percentage)}%</b>
                      </span>
                    )) : 'none'}
                  </span>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

// A section of the form. Closed, it is one line saying what is inside, so a
// glance down the form shows the whole part without opening anything. Open,
// it becomes a panel with its own outline and header bar, so it is clear
// where it starts and ends (29 Sep: in a long open section it was easy to
// lose your place). Titles and summaries sit in the same spot either way,
// so nothing jumps when a section opens.
function Section({ title, children, defaultOpen = false, summary }) {
  const [open, setOpen] = useState(defaultOpen);
  const items = (summary || []).filter(Boolean).map(x => (typeof x === 'string' ? { text: x } : x));
  const TONE = { warn: '#f59e0b', danger: '#fca5a5' };
  return (
    <div style={{
      border: `1px solid ${open ? 'rgba(20,184,166,0.45)' : 'transparent'}`,
      borderRadius: '10px',
      background: open ? '#232a33' : 'transparent',
      margin: open ? '12px 0 16px' : '0',
      overflow: 'hidden',
      transition: 'border-color 0.15s, background 0.15s',
    }}>
      <button onClick={() => setOpen(v => !v)} aria-expanded={open}
        style={{ width: '100%', background: open ? 'rgba(20,184,166,0.1)' : 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '14px', padding: '11px 14px 10px', borderBottom: `1px solid ${open ? 'rgba(20,184,166,0.3)' : '#2a3040'}`, textAlign: 'left' }}>
        <span style={{ ...s.sectionTitle, flexShrink: 0, minWidth: '156px' }}>{title}</span>
        <span style={{ flex: 1, minWidth: 0, fontSize: '12px', color: '#9ca3af', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontFamily: 'system-ui' }}>
          {!open && (items.length
            ? items.map((it, i) => (
              <span key={i}>
                {i > 0 && <span style={{ color: '#4b5563', margin: '0 7px' }}>·</span>}
                <span style={{ color: TONE[it.tone] || '#9ca3af' }}>{it.text}</span>
              </span>
            ))
            : <span style={{ color: '#4b5563', fontStyle: 'italic' }}>Nothing entered</span>)}
        </span>
        <i className={`fa-solid fa-chevron-${open ? 'up' : 'down'}`} style={{ fontSize: '10px', color: open ? '#14b8a6' : '#6b7280', flexShrink: 0 }} />
      </button>
      {open && <div style={{ padding: '16px 14px 4px' }}>{children}</div>}
    </div>
  );
}

function Field({ label, value, onChange, textarea, type, step, hint, disabled }) {
  return (
    <div style={s.field}>
      <label style={s.label}>{label}</label>
      {textarea
        ? <textarea style={{ ...s.input, minHeight: '70px', resize: 'vertical', ...(disabled ? s.inputDisabled : {}) }} value={value} onChange={e => onChange(e.target.value)} disabled={disabled} />
        : <input style={{ ...s.input, ...(disabled ? s.inputDisabled : {}) }} type={type || 'text'} step={step} value={value} onChange={e => onChange(e.target.value)} disabled={disabled} />
      }
      {hint && <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '3px' }}>{hint}</div>}
    </div>
  );
}

const s = {
  overlay:      { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  modal:        { background: '#1e2329', border: '1px solid #2a3040', borderRadius: '16px', width: '94%', maxWidth: '740px', maxHeight: '88vh', overflowY: 'auto', overflowX: 'hidden' },
  modalHeader:  { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 20px 16px', borderBottom: '1px solid #2a3040', position: 'sticky', top: 0, background: '#1e2329', zIndex: 1 },
  modalTitle:   { fontSize: '17px', fontWeight: '700' },
  closeBtn:     { background: 'none', border: 'none', color: '#6b7280', fontSize: '18px', cursor: 'pointer' },
  modalBody:    { padding: '20px 28px' },
  modalFooter:  { display: 'flex', justifyContent: 'space-between', padding: '16px 28px', borderTop: '1px solid #2a3040', position: 'sticky', bottom: 0, background: '#1e2329' },
  sectionTitle: { fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.06em', color: '#14b8a6', margin: 0 },
  row2:         { display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: '0 16px' },
  row4:         { display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0,1fr))', gap: '0 12px' },
  field:        { display: 'flex', flexDirection: 'column', gap: '5px', marginBottom: '14px' },
  label:        { fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.06em', color: '#6b7280' },
  input:        { background: '#0f1117', border: '1px solid #2a3040', borderRadius: '6px', color: '#f0f2f5', padding: '9px 12px', fontSize: '13px', fontFamily: 'system-ui, sans-serif', width: '100%', boxSizing: 'border-box' },
  inputDisabled:{ opacity: 0.55, cursor: 'not-allowed', background: '#161b22' },
  addBtn:       { background: '#14b8a6', color: '#fff', border: 'none', borderRadius: '8px', padding: '10px 18px', fontSize: '13px', fontWeight: '700', cursor: 'pointer' },
  cancelBtn:    { background: '#1e2329', border: '1px solid #2a3040', color: '#9ca3af', borderRadius: '8px', padding: '10px 16px', fontSize: '13px', cursor: 'pointer' },
  iconBtn:      { background: '#1e2329', border: '1px solid #2a3040', color: '#14b8a6', borderRadius: '6px', padding: '7px 10px', fontSize: '13px', cursor: 'pointer', flexShrink: 0 },
  inlineForm:   { background: '#0f1117', border: '1px solid #2a3040', borderRadius: '8px', padding: '14px', marginTop: '8px', marginBottom: '8px' },
  machineRow:   { display: 'flex', alignItems: 'center', gap: '8px', background: '#0f1117', border: '1px solid #2a3040', borderRadius: '6px', padding: '6px 10px' },
  machineOrd:   { fontSize: '9px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#14b8a6', minWidth: '64px' },
  miniBtn:      { background: '#1e2329', border: '1px solid #2a3040', color: '#9ca3af', borderRadius: '4px', padding: '4px 7px', fontSize: '10px', cursor: 'pointer', flexShrink: 0 },
  readOnlyNote: { fontSize: '11px', color: '#9ca3af', background: '#161b22', border: '1px solid #2a3040', borderRadius: '6px', padding: '6px 10px', marginBottom: '10px' },
  mouldHint:    { display: 'flex', alignItems: 'center', gap: '8px', background: '#0d1a1a', border: '1px solid #14b8a620', borderRadius: '6px', padding: '7px 12px', fontSize: '11px', color: '#14b8a6', marginBottom: '12px' },
  hint:         { fontSize: '10px', color: '#6b7280', marginTop: '2px' },
  warnHint:     { fontSize: '10px', color: '#f59e0b', marginTop: '2px' },
  okHint:       { fontSize: '10px', color: '#22c55e', marginTop: '2px' },
};

const rs = {
  filterBar:  { display: 'flex', gap: '6px', marginBottom: '10px', flexWrap: 'wrap' },
  search:     { flex: 2, minWidth: '120px', background: '#0f1117', border: '1px solid #2a3040', borderRadius: '6px', color: '#f0f2f5', padding: '7px 10px', fontSize: '12px', fontFamily: 'system-ui, sans-serif' },
  filter:     { flex: 1, minWidth: '100px', background: '#0f1117', border: '1px solid #2a3040', borderRadius: '6px', color: '#f0f2f5', padding: '7px 10px', fontSize: '12px' },
  item:       { border: '1px solid #2a3040', borderRadius: '8px', marginBottom: '6px', overflow: 'hidden' },
  itemSelected:{ borderColor: '#14b8a6' },
  itemRow:    { display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 12px', background: '#0f1117' },
  recipeName: { fontSize: '13px', fontWeight: '600', color: '#f0f2f5' },
  recipeMat:  { fontSize: '11px', color: '#6b7280', display: 'block' },
  expandBtn:  { background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: '12px', padding: '2px 4px', flexShrink: 0 },
  components: { background: '#1e2329', padding: '8px 12px', borderTop: '1px solid #2a3040' },
  compRow:    { display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 0', borderBottom: '1px solid #2a3040' },
  compBase:   { fontSize: '9px', fontWeight: '700', background: '#2a3040', color: '#9ca3af', padding: '1px 6px', borderRadius: '4px', flexShrink: 0 },
  compBadge:  { fontSize: '9px', fontWeight: '700', color: '#fff', padding: '1px 5px', borderRadius: '4px', flexShrink: 0 },
  compName:   { flex: 1, fontSize: '12px', color: '#9ca3af' },
  compPct:    { fontSize: '11px', color: '#6b7280' },
};
